/* Dependency Imports */
import { useMemo, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import storage from 'redux-persist/lib/storage';
import { ApolloClient, InMemoryCache, ApolloProvider, split, ApolloLink } from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';

import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { useSelector } from 'react-redux';
// @ts-ignore
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { createClient } from 'graphql-ws';
import { useAppDispatch } from '../../app/hooks';
import { setUser } from '../../features/auth/authSlice';
import { selectUser } from '../../features/auth/authSlice';
import UserAuth from './UserAuth';

/* Project Imports */
import env from '../../config/env';

const RequireAuth = () => {
  /* Redux */
  const user = useSelector(selectUser);
  /* Router */

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // The error handler reads these through refs so the Apollo client can be created once and still see the current
  // user / router - see the client below
  const userRef = useRef(user);
  userRef.current = user;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Created ONCE for the life of this layout. Building the links inline re-created the ApolloClient (and a new
  // websocket) on every render - e.g. the theme switch when the window crosses 1500px - which wiped the cache and
  // refetched everything, so the whole app looked like it reloaded.
  const client = useMemo(() => {
    const uploadLink = createUploadLink({
      uri: `${env.basePath}/graphql`,
      credentials: 'include',
      headers: { 'Apollo-Require-Preflight': 'true' },
    });

    const wsLink = new GraphQLWsLink(
      createClient({
        url: `${env.socketPath}/graphql`,
      })
    );

    const link = split(
      ({ query }) => {
        const { kind, operation }: Definition = getMainDefinition(query);
        return kind === 'OperationDefinition' && operation === 'subscription';
      },
      wsLink,
      uploadLink
    );

    let apolloClient: ApolloClient<any>;

    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (networkError) {
        // Here you may display a message to indicate network error
        if (networkError && !userRef.current) {
          navigateRef.current('/');
        }
        if (networkError.message.includes('401') || networkError.message.includes('503')) {
          dispatch(setUser(null));
          navigateRef.current('/');
        }
      }
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path, extensions }) => {
          // Here you may display a message to indicate graphql error
          console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
          if (extensions.code === 'UNAUTHENTICATED' || extensions.code === 'FORBIDDEN') {
            localStorage.clear();
            storage.removeItem('persist:root');
            apolloClient.cache.reset();
            apolloClient.clearStore().then(() => {
              dispatch(setUser(null));
              navigateRef.current('/');
            });
          }
        });
        return;
      }
      return;
    });

    apolloClient = new ApolloClient({
      link: ApolloLink.from([errorLink as any, link]),
      cache: new InMemoryCache({
        addTypename: false,
      }),
      credentials: 'include',
    });
    return apolloClient;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ApolloProvider client={client}>
      <UserAuth>
        <Outlet />
      </UserAuth>
    </ApolloProvider>
  );
  // Redirect them to the /login page, but save the current location they were
  // trying to go to when they were redirected. This allows us to send them
  // along to that page after they login, which is a nicer user experience
  // than dropping them off on the home page.
};

interface Definition {
  kind: string;
  operation?: string;
}

export default RequireAuth;
